import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { enregistrerTiers, supprimerTiers } from '../../db/repo'
import type { TypeTiers } from '../../domain/types'
import { Bascule, Carte, Champ, Confirmation, Encart, Entete, IconeChevron, IconePlus, Puce, useAction } from '../composants'
import { useDonnees } from '../donnees'

const LIBELLES: Record<TypeTiers, string> = { moi: 'vous', organisme: 'assurance / organisme', personne: 'personne' }

export function PageTiersListe() {
  const { d } = useDonnees()
  const navigate = useNavigate()
  const tries = [...d.tiers].sort((a, b) => (a.type === 'moi' ? -1 : b.type === 'moi' ? 1 : a.nom.localeCompare(b.nom, 'fr')))

  return (
    <>
      <Entete titre="Tiers" sous="Qui participe aux dépenses" retour />
      <main className="contenu">
        <Encart>
          Un tiers est quelqu’un qui prend en charge une part d’une dépense : la caisse maladie et la complémentaire (
          <strong>organismes</strong>, comptés comme remboursements), ou une <strong>personne</strong> à qui vous refacturez sa part, et
          pour qui vous pouvez éditer un justificatif.
        </Encart>
        <ul className="liste">
          {tries.map((t) => (
            <li key={t.id}>
              <Link to={`/tiers/${t.id}`} className="item">
                <div className="grandir">
                  <div className="item__principal">{t.nom}</div>
                  <div className="item__secondaire">{LIBELLES[t.type]}</div>
                </div>
                {t.archive === 1 && <Puce>archivé</Puce>}
                <span className="chevron"><IconeChevron /></span>
              </Link>
            </li>
          ))}
        </ul>
      </main>
      <button type="button" className="fab" aria-label="Nouveau tiers" onClick={() => navigate('/tiers/nouveau')}>
        <IconePlus />
      </button>
    </>
  )
}

export function PageTiers() {
  const { id } = useParams()
  const { d } = useDonnees()
  const navigate = useNavigate()
  const existant = d.tiers.find((t) => t.id === id)
  const estMoi = existant?.type === 'moi'

  const [nom, setNom] = useState(existant?.nom ?? '')
  const [type, setType] = useState<TypeTiers>(existant?.type ?? 'personne')
  const [adresse, setAdresse] = useState(existant?.adresse ?? '')
  const [courriel, setCourriel] = useState(existant?.courriel ?? '')
  const [archive, setArchive] = useState<0 | 1>(existant?.archive ?? 0)
  const [confirmer, setConfirmer] = useState(false)
  const { erreur, executer, setErreur } = useAction()

  const enregistrer = () =>
    executer(async () => {
      if (!nom.trim()) throw new Error('Donnez un nom.')
      await enregistrerTiers({ id: existant?.id, nom: nom.trim(), type, adresse, courriel, archive })
      navigate(-1)
    })

  return (
    <>
      <Entete titre={existant ? existant.nom : 'Nouveau tiers'} retour />
      <main className="contenu">
        <Carte>
          <div className="pile">
            <Champ libelle="Nom">
              <input type="text" value={nom} placeholder="Caisse maladie, ex-épouse…" onChange={(e) => setNom(e.target.value)} />
            </Champ>
            {estMoi ? (
              <p className="tres-doux">C’est vous : ce tiers reçoit le reste à charge de chaque répartition. Vos coordonnées d’en-tête se règlent dans Réglages.</p>
            ) : (
              <>
                <Bascule
                  etiquette="Type"
                  valeur={type}
                  onChange={setType}
                  options={[
                    { valeur: 'personne', libelle: 'Personne' },
                    { valeur: 'organisme', libelle: 'Assurance / organisme' },
                  ]}
                />
                <Champ libelle="Adresse" aide="Reprise telle quelle en en-tête du justificatif.">
                  <textarea rows={3} value={adresse} onChange={(e) => setAdresse(e.target.value)} />
                </Champ>
                <Champ libelle="Courriel">
                  <input type="email" value={courriel} onChange={(e) => setCourriel(e.target.value)} />
                </Champ>
                {existant && (
                  <label className="case">
                    <input type="checkbox" checked={archive === 1} onChange={(e) => setArchive(e.target.checked ? 1 : 0)} />
                    Archivé
                  </label>
                )}
              </>
            )}
          </div>
        </Carte>
        {erreur && <Encart ton="danger">{erreur}</Encart>}
        <button type="button" className="btn btn--principal btn--bloc" onClick={() => void enregistrer()}>Enregistrer</button>
        {existant && !estMoi && <button type="button" className="btn btn--danger btn--bloc" onClick={() => setConfirmer(true)}>Supprimer</button>}
      </main>
      {confirmer && existant && (
        <Confirmation
          titre="Supprimer ce tiers ?"
          message="Seul un tiers qui n’intervient dans aucune répartition ni aucun modèle peut être supprimé."
          libelleConfirmer="Supprimer"
          danger
          onAnnuler={() => setConfirmer(false)}
          onConfirmer={() => {
            setConfirmer(false)
            void supprimerTiers(existant.id)
              .then(() => navigate('/tiers', { replace: true }))
              .catch((e: unknown) => setErreur(e instanceof Error ? e.message : String(e)))
          }}
        />
      )}
    </>
  )
}
